import { useCallback, useRef, useMemo } from 'react';

import { getFile } from '@/requests/cloud-storage/getFile';
import { getMediaFromFlowId } from '@/requests/magic-flow/getMediaFromFlowId';
import { setAppStore, getAppStore } from '@/store';
import { createContentFile } from '@/utils/media/createContentFile';
import { extractUrlParams } from '@/utils/ui/extractUrlParams';
import { DeeplinkKeys } from '@/consts/deepLinks';

// Module-level flag to prevent double-execution in React Strict Mode
let hasHandledNotification = false;

const useNotificationHandler = () => {
  // Memoize the extraction to avoid creating new objects on every render
  const taskId = useMemo(() => {
    const urlParams = extractUrlParams([DeeplinkKeys.taskId]);
    return urlParams[DeeplinkKeys.taskId];
  }, []);

  // Ref to track if handler is currently processing
  const isProcessingRef = useRef(false);

  const notificationHandler = useCallback(async () => {
    // Guard against missing taskId
    if (!taskId) {
      return;
    }

    // Guard against multiple calls (React Strict Mode, re-renders)
    if (hasHandledNotification || isProcessingRef.current) {
      return;
    }

    isProcessingRef.current = true;
    hasHandledNotification = true;

    // Set loading state and hasAutoSelectedPreset immediately to prevent other hooks from changing screen
    setAppStore({ screen: 'loading', hasAutoSelectedPreset: true });

    try {
      const media = await getMediaFromFlowId(taskId);

      if (!media) {
        setAppStore({ screen: 'library' });
        return;
      }

      const driveFile = media.driveUid
        ? await getFile({ uid: media.driveUid })
        : null;

      const { contentFile } = createContentFile({
        media,
        fileName: driveFile?.name,
      });

      setAppStore({
        resultFile: media,
        screen: 'result',
        hasAutoSelectedPreset: true,
      });

      // Add to content files if not already there
      const { contentFiles, addContentFile } = getAppStore();

      const exists = contentFiles.some((f) => f.uuid === contentFile.uuid);
      if (!exists) {
        addContentFile(contentFile);
      }
    } catch {
      // Failed to load notification result, go to library
      setAppStore({ screen: 'library' });
    } finally {
      isProcessingRef.current = false;
    }
  }, [taskId]);

  return { notificationHandler, hasTaskId: Boolean(taskId) };
};

// Reset function for testing/cleanup (e.g., when navigating away)
const resetNotificationHandler = () => {
  hasHandledNotification = false;
};

export { useNotificationHandler, resetNotificationHandler };
